"use client";

import { CheckCircle, XCircle, MapPin } from "lucide-react";
import { useAddressSearch } from "@/hooks/useAddressSearch";
import { useRef, useState, useEffect } from "react";

export function AddressDeliveryChecker() {
  const {
    query,
    setQuery,
    suggestions,
    isSearching,
    hasResolvedSearch,
    searchError,
    selectedAddress,
    selectAddress,
    checkDelivery,
    delivers,
    isCheckingDelivery,
    deliveryError,
  } = useAddressSearch();

  const [showDropdown, setShowDropdown] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const cooldownRef = useRef<boolean>(false);
  const lastAutoCheckedAddressRef = useRef<string | null>(null);
  const trimmedQuery = (query ?? "").trim();
  const shouldShowDropdown = !selectedAddress && trimmedQuery.length >= 3 && (
    suggestions.length > 0 || (hasResolvedSearch && !isSearching)
  );

  useEffect(() => {
    setShowDropdown(shouldShowDropdown);
  }, [shouldShowDropdown]);

  useEffect(() => {
    if (!selectedAddress) {
      lastAutoCheckedAddressRef.current = null;
      return;
    }

    if (lastAutoCheckedAddressRef.current === selectedAddress.id) {
      return;
    }

    lastAutoCheckedAddressRef.current = selectedAddress.id;
    void handleCheck();
  }, [selectedAddress]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
    selectAddress(null);
  };

  const handleSelectAddress = (suggestion: typeof suggestions[0]) => {
    selectAddress(suggestion);
    setShowDropdown(false);
  };

  const handleCheck = async () => {
    if (!selectedAddress || cooldownRef.current) return;

    cooldownRef.current = true;
    await checkDelivery();

    setTimeout(() => {
      cooldownRef.current = false;
    }, 1200);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && selectedAddress && !isCheckingDelivery) {
      handleCheck();
    }
  };

  // Button state
  const getButtonState = () => {
    if (isCheckingDelivery) return null;
    if (delivers === true) return "available";
    if (delivers === false) return "unavailable";
    return null;
  };

  const buttonState = getButtonState();

  // Message visibility
  const showSuccessMessage = !isCheckingDelivery && delivers === true;
  const showErrorMessage = !isCheckingDelivery && delivers === false && !deliveryError;
  const showGenericError = !isCheckingDelivery && (deliveryError || searchError);

  return (
    <div className="max-w-md mx-auto relative z-20">
      {/* Keep the original cohesive styling, but render results as an overlay */}
      <div
        ref={containerRef}
        className="relative w-full"
      >
        <div className="relative flex items-center overflow-hidden rounded-[28px] border border-white/15 bg-white/10 backdrop-blur-md transition-all duration-300">
          <input
            ref={inputRef}
            type="text"
            value={query || ""}
            onChange={handleInputChange}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleKeyPress(e);
            }}
            onFocus={() => shouldShowDropdown && setShowDropdown(true)}
            placeholder="Enter your delivery address..."
            className="w-full bg-transparent text-white placeholder-white/50 focus:outline-none text-base py-4 pl-6 pr-28"
          />

          {/* Check button */}
          <button
            onClick={handleCheck}
            disabled={isSearching || !selectedAddress || isCheckingDelivery}
            className={`absolute right-1.5 top-1/2 -translate-y-1/2 min-w-[88px] px-5 py-3 text-white text-sm font-semibold rounded-[22px] disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-300 flex items-center justify-center ${
              buttonState === "available"
                ? "bg-emerald-500 hover:bg-emerald-600"
                : buttonState === "unavailable"
                ? "bg-red-500/90 hover:bg-red-600"
                : "bg-white/15 hover:bg-white/25 backdrop-blur-sm"
            }`}
          >
            {isSearching ? (
              <div className="w-4 h-4 border-2 border-white/60 border-t-transparent rounded-full animate-spin" />
            ) : isCheckingDelivery ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : buttonState === "available" ? (
              <CheckCircle className="w-5 h-5 text-white" />
            ) : buttonState === "unavailable" ? (
              <XCircle className="w-5 h-5 text-white" />
            ) : (
              "Check"
            )}
          </button>
        </div>

        {/* Results panel overlays below the input instead of pushing layout */}
        {showDropdown && (
          <div className="absolute left-0 right-0 top-full mt-1.5 z-50 overflow-hidden rounded-[28px] border border-white/15 bg-white/10 shadow-2xl backdrop-blur-md">
            <div className="mx-4 border-t border-white/10" />
            {suggestions.length > 0 ? (
              <div className="max-h-56 overflow-y-auto py-1">
                {suggestions.map((suggestion) => (
                  <button
                    key={suggestion.id}
                    onClick={() => handleSelectAddress(suggestion)}
                    className="w-full px-5 py-3 text-left hover:bg-white/10 transition-colors duration-150 flex items-center gap-3 group"
                  >
                    <MapPin className="w-4 h-4 text-white/40 group-hover:text-white/60 transition-colors flex-shrink-0" />
                    <span className="text-white/80 text-sm truncate group-hover:text-white transition-colors">
                      {suggestion.displayName}
                    </span>
                  </button>
                ))}
              </div>
            ) : (
              <div className="py-5 text-center">
                <p className="text-white/40 text-sm">No addresses found</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Result Messages */}
      <div className="mt-3 min-h-[24px]">
        {showSuccessMessage && (
          <p className="text-center text-sm text-emerald-300 font-medium animate-in fade-in duration-200">
            ✓ Great news! We deliver to your area.
          </p>
        )}
        {showErrorMessage && (
          <p className="text-center text-sm text-red-300 font-medium animate-in fade-in duration-200">
            Sorry, we don&apos;t deliver to this area yet. We&apos;re expanding soon!
          </p>
        )}
        {showGenericError && (
          <p className="text-center text-sm text-amber-300 font-medium animate-in fade-in duration-200">
            {deliveryError || searchError}
          </p>
        )}
      </div>
    </div>
  );
}
